import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../../contexts/config-context';
import ListingAPI from '../../../axios/listing';
import { formatNumber, normalize, LISTING_ROUTES, MIN_ROOM_IDS, PRICE_PRESETS, PRICE_UNITS } from '../home-utils';
import { areaContext, areaSummary, isAreaSelected, placeAsArea, useAreaSuggestions, withArea, MIN_AREA_QUERY } from '../../search-page/area-search';

const Chevron = ({ open }) => (
    <svg className={`hs-dd-chevron ${open ? 'open' : ''}`} width="12" height="8" viewBox="0 0 12 8" fill="none">
        <path d="M1 1.5 6 6.5 11 1.5" stroke="#5B6779" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const Check = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0073F0" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
);

const Dropdown = ({ label, value, open, onToggle, panelClass, children }) => (
    <div className='hs-dd'>
        <button className={`hs-dd-trigger ${open ? 'open' : ''}`} onClick={onToggle}>
            <span className='hs-field-label'>{label}</span>
            <span className='hs-dd-value'><span>{value}</span><Chevron open={open} /></span>
        </button>
        {open && <div className={`hs-dd-panel ${panelClass}`}>{children}</div>}
    </div>
);

export const HeroSearch = ({ tab, setTab, places, propertyTypes, typeCounts, popularPlaces }) => {
    const { t } = useTranslation(["home"]);
    const { searchData, num_rooms_options } = useContext(ConfigContext);
    const navigate = useNavigate();
    const ddRef = useRef(null);

    const [dd, setDd] = useState(null);
    const [place, setPlace] = useState("");
    // the parts of the city that were picked instead of the whole city
    const [areas, setAreas] = useState([]);
    const cityInputRef = useRef(null);
    const [cityQuery, setCityQuery] = useState('');
    const [areaResults] = useAreaSuggestions(cityQuery);
    const [propType, setPropType] = useState(null);
    const [maxPrice, setMaxPrice] = useState('');
    const [minRooms, setMinRooms] = useState(null);
    const [count, setCount] = useState(0);

    useEffect(() => {
        const close = (e) => {
            if (ddRef.current && !ddRef.current.contains(e.target)) setDd(null);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    // short text filters the cities; anything longer is also looked up among the parts of every city
    const searchingAreas = cityQuery.trim().length >= MIN_AREA_QUERY;
    const cityOptions = searchingAreas
        ? areaResults
        : [{ type: 'place', id: null, label: t('any_city') }, ...areas, ...places.filter(p => normalize(p.label).includes(normalize(cityQuery))).map(placeAsArea)];
    const pickArea = (o) => {
        const next = o.id === null ? { place: "" } : withArea(o, { place, areas });
        setPlace(next.place);
        setAreas(next.areas || []);
        setCityQuery('');
        setDd(null);
    };

    const buildSearchData = (overrides = {}) => ({
        ...searchData,
        listing_type: String(tab + 1),
        place,
        subplaces: areas.map(a => a.value),
        areas: areas.length > 0 ? areas : undefined,
        property_type: propType ? [propType] : [],
        max_price: maxPrice,
        min_rooms: minRooms,
        ...overrides,
    });

    useEffect(() => {
        const timer = setTimeout(() => {
            ListingAPI.getListingsSearchCount(buildSearchData())
                .then(res => setCount(res.data.total_listings))
                .catch(err => console.log(err));
        }, 300);
        return () => clearTimeout(timer);
    }, [tab, place, areas, propType, maxPrice, minRooms]);

    const search = (overrides) => {
        sessionStorage.removeItem('currentPage');
        navigate(LISTING_ROUTES[tab], { state: { data: buildSearchData(overrides) } });
    };

    const toggle = (key) => setDd(dd === key ? null : key);
    const tabs = [t('tab_sale'), t('tab_rent'), t('tab_daily')];
    const unit = tab === 0 ? '€' : `€ ${t(PRICE_UNITS[tab])}`;
    const typeLabel = propertyTypes.find(o => o.id === propType)?.label || t('any_type');
    const roomLabel = (id) => `${num_rooms_options.find(o => o.id === id)?.label}+`;

    return (
        <div className='hs-search'>
            <div className='hs-search-card'>
                <div className='hs-search-tabs'>
                    {tabs.map((label, i) => (
                        <button key={label} className={i === tab ? 'on' : ''}
                            onClick={() => { setTab(i); setMaxPrice(''); }}>{label}</button>
                    ))}
                </div>
                <div className='hs-search-fields'>
                    <div ref={ddRef} className='hs-dd-group'>
                        <Dropdown label={t('property_type')} value={typeLabel} open={dd === 'type'}
                            onToggle={() => toggle('type')} panelClass='left'>
                            {[{ id: null, label: t('any_type') }, ...propertyTypes].map(o => (
                                <button key={`type-${o.id}`} className={`hs-type-opt ${o.id === propType ? 'on' : ''}`}
                                    onClick={() => { setPropType(o.id); setDd(null); }}>
                                    <span className='hs-type-opt-text'>
                                        <span className='hs-type-opt-label'>{o.label}</span>
                                        {typeCounts[o.id] !== undefined &&
                                            <span className='hs-type-opt-count'>{formatNumber(typeCounts[o.id])} {t('listings_count')}</span>}
                                    </span>
                                    {o.id === propType && <Check />}
                                </button>
                            ))}
                        </Dropdown>
                        <Dropdown label={t('location')} value={areas.length > 0 ? areaSummary(areas, 1) : place ? place.label : t('any_city')} open={dd === 'city'}
                            onToggle={() => toggle('city')} panelClass='left'>
                            <div className='hs-price-input hs-city-search'>
                                <input ref={cityInputRef} autoFocus placeholder={t('search_city')} value={cityQuery}
                                    onChange={(e) => setCityQuery(e.target.value)} />
                            </div>
                            <div className='hs-dd-scroll'>
                                {cityOptions.map(o => {
                                    const on = o.id === null ? !place : isAreaSelected(o, { place, areas });
                                    const context = o.id !== null && areaContext(o);
                                    return (
                                        <button key={`city-${o.type}-${o.id}`} className={`hs-type-opt ${on ? 'on' : ''}`} onClick={() => pickArea(o)}>
                                            <span className='hs-type-opt-text'>
                                                <span className='hs-type-opt-label'>{o.label}</span>
                                                {context && <span className='hs-type-opt-count'>{context}</span>}
                                            </span>
                                            {on && <Check />}
                                        </button>
                                    );
                                })}
                            </div>
                        </Dropdown>
                        <Dropdown label={t('price_to')} value={maxPrice ? `${formatNumber(maxPrice)} ${unit}` : t('any_price')}
                            open={dd === 'price'} onToggle={() => toggle('price')} panelClass='center'>
                            <p className='hs-dd-title'>{t('max_price')}</p>
                            <div className='hs-chips cols-2'>
                                <button className={!maxPrice ? 'on' : ''} onClick={() => { setMaxPrice(''); setDd(null); }}>{t('any_price')}</button>
                                {PRICE_PRESETS[tab].map(p => (
                                    <button key={p} className={String(p) === maxPrice ? 'on' : ''}
                                        onClick={() => { setMaxPrice(String(p)); setDd(null); }}>{formatNumber(p)} €</button>
                                ))}
                            </div>
                            <div className='hs-price-input'>
                                <input placeholder={t('enter_amount')} inputMode='numeric' value={maxPrice}
                                    onChange={(e) => setMaxPrice(e.target.value.replace(/\D/g, ''))} />
                                <span>{unit}</span>
                            </div>
                        </Dropdown>
                        <Dropdown label={t('rooms')} value={minRooms ? roomLabel(minRooms) : t('all_rooms')}
                            open={dd === 'rooms'} onToggle={() => toggle('rooms')} panelClass='right'>
                            <p className='hs-dd-title'>{t('min_rooms')}</p>
                            <div className='hs-chips cols-3'>
                                <button className={!minRooms ? 'on' : ''} onClick={() => { setMinRooms(null); setDd(null); }}>{t('all_rooms')}</button>
                                {MIN_ROOM_IDS.map(id => (
                                    <button key={id} className={id === minRooms ? 'on' : ''}
                                        onClick={() => { setMinRooms(id); setDd(null); }}>{roomLabel(id)}</button>
                                ))}
                            </div>
                        </Dropdown>
                    </div>
                    <button className='hs-search-btn' onClick={() => search()}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                        {t('search_btn', { n: formatNumber(count) })}
                    </button>
                </div>
                <div className='hs-search-popular'>
                    {popularPlaces.length > 0 && <span>{t('popular')}</span>}
                    {popularPlaces.map(p => (
                        <button key={p.id} className='hs-popular-link' onClick={() => search(withArea(placeAsArea(p), {}))}>{p.label}</button>
                    ))}
                    <span className='hs-spacer'></span>
                    <button className='hs-detailed-link' onClick={() => search()}>{t('detailed_search')} →</button>
                </div>
            </div>
        </div>
    );
};
